// Deterministic NFT generation from a user's investments.
// - Each ACTIVE investment gets split into $10-multiple chunks (or one exact chunk if amount < step).
// - Each chunk becomes an NFT with a unique ID, a random gif, and a rarity tier.
// - Output is stable UNTIL `shuffleKey` changes. We pass basic.lastTradeTime as the key,
//   so every successful Start Trade rerolls the split, gifs, IDs and rarities.

import { NFT_GIFS } from '../config/appConfig';
import { toNumberSafe } from './format';

// Simple 32-bit FNV-1a hash for deterministic pseudo-randomness (no external dep).
function fnv1a(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h >>> 0;
}

// Split a dollar amount into 1..N chunks of $step multiples that sum to amount.
// Uses the passed seed for stability; small amounts collapse to a single chunk.
function splitAmount(amount, step, seed) {
  const total = Math.floor(amount);
  if (total <= 0) return [];
  if (total < step) return [total];

  const units = Math.floor(total / step);           // number of $step units
  const remainder = total - units * step;

  // Aim for 1..min(units,6) chunks so NFT count feels varied but not spammy
  const maxChunks = Math.min(units, 6);
  const nChunks = Math.max(1, 1 + (seed % maxChunks));

  const chunkUnits = new Array(nChunks).fill(1);    // each chunk gets at least 1 unit
  let left = units - nChunks;
  let s = seed >>> 0;
  while (left > 0) {
    s = (s * 1664525 + 1013904223) >>> 0;
    const idx = s % nChunks;
    chunkUnits[idx] += 1;
    left--;
  }

  const chunks = chunkUnits.map(u => u * step);
  if (remainder > 0) {
    let maxIdx = 0;
    for (let i = 1; i < chunks.length; i++) if (chunks[i] > chunks[maxIdx]) maxIdx = i;
    chunks[maxIdx] += remainder;
  }
  return chunks;
}

/**
 * Generate NFTs for a user's active investments.
 * @param {string} wallet       connected wallet address
 * @param {Array}  investments  [{ amount: BigNumber, active, timestamp, ... }, ...]
 * @param {number} step         chunk denomination (default 10)
 * @param {number|string} shuffleKey  changes here reshuffle the whole collection
 *                                   (pass basic.lastTradeTime so Start Trade rerolls)
 */
export function generateNFTs(wallet, investments, step = 10, shuffleKey = 0) {
  if (!wallet || !investments || !investments.length) return [];
  const sk = String(shuffleKey || 0);
  const out = [];

  investments.forEach((inv, invIdx) => {
    if (!inv.active) return;
    const dollars = Math.floor(toNumberSafe(inv.amount));
    const seed = fnv1a(`${wallet}:${invIdx}:${inv.timestamp || 0}:${sk}`);
    const chunks = splitAmount(dollars, step, seed);

    chunks.forEach((val, chunkIdx) => {
      const chunkSeed = fnv1a(`${wallet}:${invIdx}:${chunkIdx}:${val}:${sk}`);
      const gif = NFT_GIFS[chunkSeed % NFT_GIFS.length];
      const uniqueNum = (chunkSeed % 900000) + 100000; // 6-digit
      const rarityRoll = chunkSeed % 100;
      const rarity =
        rarityRoll < 3  ? 'Mythic' :
        rarityRoll < 12 ? 'Legendary' :
        rarityRoll < 30 ? 'Epic' :
        rarityRoll < 60 ? 'Rare' : 'Common';

      out.push({
        id: `ADR-${uniqueNum}`,
        gif,
        value: val,
        invIndex: invIdx,
        chunkIndex: chunkIdx,
        name: `Adrenaline #${uniqueNum}`,
        rarity
      });
    });
  });

  return out;
}
